import { DEFAULT_QUOTE_SECTIONS, Quote } from '../types';
import { calculateQuoteTotals, computePosNumbers } from './quoteLogic';
import { mergeQuoteUpdate } from './quoteMerge';

/**
 * Speicherlogik des Angebotseditors, frei von React und Netz.
 *
 * Der Editor haelt zwei Staende: `base` (was zuletzt nachweislich auf dem
 * Server stand) und `draft` (was der Nutzer gerade sieht). Gespeichert wird
 * nur, was sich in den dreizehn editierbaren Feldern von `base` unterscheidet.
 */
export const QUOTE_EDITABLE_KEYS = [
  'title', 'quoteDate', 'validUntil', 'followUpDate', 'discountPercent', 'taxRate',
  'introText', 'outroText', 'paymentTerms', 'items', 'details', 'netTotal', 'grossTotal',
] as const satisfies readonly (keyof Quote)[];

export type QuoteEditableKey = (typeof QUOTE_EDITABLE_KEYS)[number];

/** Im gesperrten Angebot (versendet/abgelaufen/angenommen/abgelehnt) aenderbar. */
export const QUOTE_LOCKED_EDITABLE_KEYS: readonly QuoteEditableKey[] = ['followUpDate'];

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function copyKey<K extends keyof Quote>(target: Partial<Quote>, source: Quote, key: K): void {
  target[key] = source[key];
}

/** Editierbare Felder, in denen sich `next` von `base` unterscheidet. */
export function diffQuoteUpdates(next: Quote, base: Quote): Partial<Quote> {
  const updates: Partial<Quote> = {};
  QUOTE_EDITABLE_KEYS.forEach((k) => {
    if (!same(next[k], base[k])) copyKey(updates, next, k);
  });
  return updates;
}

/**
 * Der zu speichernde Stand: Positionsnummern festgeschrieben, Sektionen in
 * details, Endsummen OHNE NEP (calculateQuoteTotals) — plus die Differenz zu
 * `base`.
 */
export function prepareQuoteSave(draft: Quote, base: Quote): { next: Quote; updates: Partial<Quote> } {
  const sections = draft.details?.sections ?? DEFAULT_QUOTE_SECTIONS;
  const totals = calculateQuoteTotals(draft.items || [], draft.discountPercent || 0, draft.taxRate || 0);
  const next: Quote = {
    ...draft,
    items: computePosNumbers(sections, draft.items || []),
    details: { ...(draft.details || {}), sections },
    netTotal: totals.netTotal,
    grossTotal: totals.grossTotal,
  };
  return { next, updates: diffQuoteUpdates(next, base) };
}

/**
 * Eingaben, die WAEHREND eines Speicherns passiert sind (`current` weicht von
 * dem abgeschickten `sent` ab), auf den neuen Serverstand `onto` uebertragen.
 *
 * Echte Drei-Wege-Zusammenfuehrung (quoteMerge.mergeQuoteUpdate) mit `sent`
 * als Ausgangsstand: Positionen nach Id, details je Schluessel. Ein Wegkopieren
 * der ganzen items-/details-Spalte wuerde, was ein Anderer gerade
 * hinzugefuegt hat und in `onto` steckt, wieder entfernen — und der naechste
 * Autosave loeschte es dann auch auf dem Server.
 *
 * Ohne wirksame Eingabe ist das Ergebnis `onto` selbst (Referenz) — damit der
 * Editor danach wieder als "nicht geaendert" gilt.
 */
export function rebaseInFlightEdits(sent: Quote, current: Quote, onto: Quote): Quote {
  if (current === sent) return onto;
  const local = diffQuoteUpdates(current, sent);
  if (Object.keys(local).length === 0) return onto;
  const patch = mergeQuoteUpdate(sent, local, onto);
  const rebased: Quote = { ...onto, ...patch };
  return Object.keys(diffQuoteUpdates(rebased, onto)).length === 0 ? onto : rebased;
}

export interface SaveSuccessInput {
  /** Der Draft, der abgeschickt wurde. */
  sent: Quote;
  /** Der daraus berechnete Speicherstand (prepareQuoteSave). */
  next: Quote;
  /** Der Draft JETZT — kann seit dem Abschicken weitergetippt sein. */
  current: Quote;
  updatedAt: string;
  /** Vom Dienst zusammengefuehrter Serverstand, falls parallel geaendert wurde. */
  merged: Quote | null;
}

/**
 * Neuer `base` und neuer `draft` nach einem erfolgreichen Speichern.
 * EINMAL berechnen und `base` unveraendert weiterreichen: ohne weitere
 * Eingaben ist `draft === base` (Referenz). Ein zweites Berechnen erzeugte ein
 * neues, gleiches Objekt — der Editor hielte sich dann fuer geaendert und
 * speicherte alle 2 s erneut.
 */
export function resolveSaveSuccess({ sent, next, current, updatedAt, merged }: SaveSuccessInput): { base: Quote; draft: Quote } {
  const base = settleSaveBase(next, updatedAt, merged);
  return { base, draft: rebaseInFlightEdits(sent, current, base) };
}

/**
 * Nur der neue Serverstand nach einem erfolgreichen Schreiben — ohne die
 * Nachbearbeitung des Entwurfs. Der Editor uebernimmt ihn SOFORT als Basis
 * und Anker: scheitert danach etwas beim Nachbearbeiten, bleibt der Anker
 * trotzdem richtig.
 */
export function settleSaveBase(next: Quote, updatedAt: string, merged: Quote | null): Quote {
  return merged
    ? (merged.updatedAt ? merged : { ...merged, updatedAt })
    : { ...next, updatedAt };
}

function copyPartialKey<K extends keyof Quote>(target: Partial<Quote>, source: Partial<Quote>, key: K): void {
  target[key] = source[key];
}

function pickKeys(updates: Partial<Quote>, keys: readonly QuoteEditableKey[]): Partial<Quote> {
  const out: Partial<Quote> = {};
  keys.forEach((k) => {
    if (k in updates) copyPartialKey(out, updates, k);
  });
  return out;
}

/** Was eine lokale Sicherung mitbringt (quoteDraftBackup.QuoteDraftBackup). */
export interface RestorableBackup {
  draft: Quote;
  /** Serverstand, von dem aus der Entwurf bearbeitet wurde. Fehlt bei Altsicherungen. */
  base?: Quote | null;
}

/**
 * Eine lokale Sicherung auf den AKTUELLEN Serverstand legen.
 *
 * Mit gesichertem Ausgangsstand: nur die Aenderungen des Entwurfs gegenueber
 * SEINEM Ausgangsstand, drei-Wege-zusammengefuehrt auf den Server — Felder,
 * die der Entwurf nie angefasst hat, bleiben beim Server, auch wenn der
 * Entwurf dort einen alten Wert traegt.
 *
 * Altsicherung ohne Ausgangsstand: jedes vom Server abweichende Feld (der
 * Editor zeigt dazu den Hinweis, dass das Angebot sich geaendert haben kann).
 *
 * Gesperrtes Angebot: nur das Nachfassdatum. Ohne Wirkung: `server` selbst.
 * Gespeichert wird danach ganz normal mit `server` als Basis und Anker.
 */
export function restoreQuoteDraftBackup(server: Quote, backup: RestorableBackup, locked: boolean): Quote {
  const keys: readonly QuoteEditableKey[] = locked ? QUOTE_LOCKED_EDITABLE_KEYS : QUOTE_EDITABLE_KEYS;
  const from = backup.base ?? server;
  const local = pickKeys(diffQuoteUpdates(backup.draft, from), keys);
  if (Object.keys(local).length === 0) return server;
  const patch = backup.base
    ? mergeQuoteUpdate(backup.base, local, server)
    : mergeQuoteUpdate(server, local, server);
  const restored: Quote = { ...server, ...patch };
  // Stehen die Aenderungen schon so auf dem Server: nichts wiederherzustellen.
  return Object.keys(diffQuoteUpdates(restored, server)).length === 0 ? server : restored;
}

export function hasRestorableChanges(server: Quote, backup: RestorableBackup, locked: boolean): boolean {
  return restoreQuoteDraftBackup(server, backup, locked) !== server;
}

/**
 * Was der Editor mit einer lokalen Sicherung anfangen kann:
 *  - 'none'    — nichts mehr wiederherzustellen (steht so auf dem Server);
 *  - 'full'    — alle Aenderungen lassen sich uebernehmen;
 *  - 'blocked' — das Angebot ist gesperrt, und die Sicherung enthaelt mehr
 *                als das Nachfassdatum. Dann NICHT teilweise uebernehmen und
 *                nicht wegwerfen: nach "Bearbeiten" (Entsperren) geht es.
 * Ob eine Sicherung erledigt ist, entscheidet immer der VOLLE Vergleich —
 * unabhaengig von der Sperre.
 */
export type BackupRestoreMode = 'none' | 'full' | 'blocked';

export function backupRestoreMode(server: Quote, backup: RestorableBackup, locked: boolean): BackupRestoreMode {
  const full = restoreQuoteDraftBackup(server, backup, false);
  if (full === server) return 'none';
  if (!locked) return 'full';
  const underLock = restoreQuoteDraftBackup(server, backup, true);
  return Object.keys(diffQuoteUpdates(full, underLock)).length === 0 ? 'full' : 'blocked';
}

/**
 * Eingefrorenen Stand (Angebotswechsel/Unmount) auf den Serverstand legen, den
 * ein noch laufendes Speichern desselben Angebots geliefert hat.
 *
 * Ausgangsstand des Drei-Wege-Abgleichs ist der Entwurf, den JENES Speichern
 * tatsaechlich abgeschickt hat (`inFlightSent`) — nicht die alte Basis: sonst
 * ginge ein waehrenddessen getipptes Zuruecknehmen (A→B abgeschickt, dann
 * wieder A) verloren, weil alte Basis und Entwurf beide A tragen.
 * Was jenes Speichern von Anderen zusammengefuehrt hat, bleibt erhalten.
 * `inFlightSent === null` (kein erfolgreiches Speichern dazwischen): der
 * eingefrorene Entwurf selbst. Ergebnis === newBase: nichts mehr zu speichern.
 */
export function rebaseSnapshotOnto(inFlightSent: Quote | null, frozenDraft: Quote, newBase: Quote): Quote {
  if (!inFlightSent) return frozenDraft;
  return rebaseInFlightEdits(inFlightSent, frozenDraft, newBase);
}

/**
 * Gehoert das Ergebnis eines laufenden Aufrufs (Annahme, Statuswechsel) noch
 * in diesen Editor? Nur wenn er noch offen ist UND dasselbe Angebot zeigt,
 * fuer das der Aufruf gestartet wurde. Sonst bekommt nur die Liste den Stand.
 */
export function resultBelongsToEditor(startedForId: string, currentId: string, mounted: boolean): boolean {
  return mounted && startedForId === currentId;
}

/**
 * Angebotsstand nach einer gescheiterten Annahme, die schon geschrieben hatte:
 * die gelesene Zeile, sonst die Basis mit dem neuen Anker (und ggf. Status
 * "angenommen", wenn das Projekt schon existiert).
 */
export function quoteAfterAcceptFailure(
  base: Quote,
  failed: { updatedAt: string; quote: Quote | null; accepted: boolean; status?: Quote['status'] },
): Quote {
  if (failed.quote) return failed.quote;
  // Teilbeauftragung: das Projekt steht, der Status ist 'partial' — nicht 'accepted'.
  return { ...base, updatedAt: failed.updatedAt, ...(failed.accepted ? { status: failed.status ?? ('accepted' as Quote['status']) } : {}) };
}

/**
 * So lange speichern, bis der Entwurf dem gespeicherten Stand entspricht —
 * hoechstens `maxRounds` Runden (wer schneller tippt als gespeichert wird,
 * bekommt ein "nicht gespeichert" statt einer Endlosschleife).
 */
export async function flushUntilSettled(
  saveOnce: () => Promise<boolean>,
  isSettled: () => boolean,
  isBlocked: () => boolean,
  maxRounds = 3,
): Promise<boolean> {
  for (let round = 0; round < maxRounds; round++) {
    if (isBlocked()) return isSettled();
    if (!(await saveOnce())) return false;
    if (isSettled()) return true;
  }
  return false;
}
